import React from 'react';
import { XCircle, Check, AlertTriangle, ShieldCheck } from 'lucide-react';

const Comparison: React.FC = () => {
  const problems = [
    { title: "Regulatory Maze", desc: "FSSAI feels designed to intimidate. Approvals and labeling requirements are confusing." },
    { title: "Zero Guidance on Formulation", desc: "Ingredient sourcing? Dosages? Flavor? You're left Googling and hoping for the best." },
    { title: "Manufacturers Who Ignore You", desc: "'Come back when you order 10,000 units.' Not helpful for validating your product." },
    { title: "Amateur Packaging", desc: "Bad packaging kills conversions. If it looks homemade, customers scroll past." },
    { title: "Vanishing Timelines", desc: "'2 months' becomes six. Your momentum dies. Your window closes." },
    { title: "Inconsistent Quality", desc: "Batch 1 was great. Batch 3 has issues. You can't scale if you can't trust the product." }
  ];

  const solutions = [
    { title: "Compliance-First Manufacturing", desc: "Every product meets FSSAI & GMP standards. We handle licensing, testing, and documentation." },
    { title: "Transparent Execution", desc: "Clear quotes with zero hidden fees. Production schedules you can actually trust." },
    { title: "Quality That's Repeatable", desc: "Batch testing with COA. Strict quality control. Same premium product, every single time." },
    { title: "Founder-Friendly Minimums", desc: "Start small with low MOQs, validate demand, then scale. No pressure to over-invest." },
    { title: "End-to-End Support", desc: "From formulation to fulfillment. We grow as your brand grows." }
  ];

  return (
    <section className="py-24 bg-gray-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
           <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-red-50 border border-red-100 mb-6">
            <span className="text-red-800 text-xs font-bold uppercase tracking-widest">
                The Reality Check
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            The Manufacturing Pain Points <br className="hidden md:block" /> You're Tired Of
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Let's talk about what's actually broken in India's supplement manufacturing industry—and why you're reading this.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Problems Card */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group border border-gray-100">
             {/* Slick Left Frame - Red */}
            <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="flex items-center mb-8 pb-8 border-b border-gray-100">
                 <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mr-5 shadow-sm border border-red-100">
                    <AlertTriangle className="w-7 h-7 text-red-500" strokeWidth={2} />
                 </div>
                 <div>
                    <h3 className="text-2xl font-extrabold text-gray-900">The Industry Norm</h3>
                    <p className="text-red-700 font-medium text-sm">Frustration & Uncertainty</p>
                 </div>
            </div>

            <ul className="space-y-6">
              {problems.map((item, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="mt-1 mr-4 min-w-[24px]">
                     <XCircle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">{item.title}</h4>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Card */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group border-2 border-orange-100">
             {/* Slick Left Frame - Orange */}
            <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
             <div className="flex items-center mb-8 pb-8 border-b border-gray-100">
                 <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mr-5 shadow-sm border border-orange-100">
                    <ShieldCheck className="w-7 h-7 text-orange-600" strokeWidth={2} />
                 </div>
                 <div>
                    <h3 className="text-2xl font-extrabold text-gray-900">The BuildMySupplement Standard</h3>
                    <p className="text-orange-800 font-medium text-sm">Clarity & Confidence</p>
                 </div>
            </div>

            <ul className="space-y-6">
              {solutions.map((item, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="mt-1 mr-4 min-w-[24px]">
                     <div className="bg-orange-100 rounded-full p-1">
                        <Check className="w-4 h-4 text-orange-600 stroke-[3]" />
                     </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">{item.title}</h4>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            
            <div className="mt-10 pt-8 border-t border-gray-100 text-center">
                 <p className="text-gray-900 font-medium">Ready to switch to the better way?</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;